import type { ARCPattern, ARCPatternResult, LiveSnapshot } from "@/arc/arc.types";

export const blockedJobsPattern: ARCPattern = {
  id: "blocked_jobs",
  label: "Hard-Blocked Jobs",
  owner: "ops",
  urgency: "high",

  evaluate(snapshot: LiveSnapshot): ARCPatternResult {
    const { jobs, issues } = snapshot;
    const openJobs = jobs.filter((j) => j.currentStage !== "Completed");

    const urgentIssueJobIds = new Set(
      issues
        .filter((i) => (i.status === "Open" || i.status === "In Progress") && i.severity === "Urgent" && !!i.blockerCategory)
        .map((i) => i.jobId)
    );

    const blocked = openJobs.filter((j) => {
      const hasUrgentBlock = urgentIssueJobIds.has(j.id);
      const materialBlocked = j.materialReadiness === "Missing Items";
      const permitBlocked =
        j.permitRequired &&
        (j.permitStatus === "Rejected" || j.permitStatus === "Expired");
      return hasUrgentBlock || materialBlocked || permitBlocked;
    });

    const jobRev = (j: typeof jobs[0]) =>
      j.financials?.contractValue ?? j.payoutAmount ?? 0;

    const amountAtRisk = blocked.reduce((s, j) => s + jobRev(j), 0);

    const reasons = blocked.map((j) => {
      const parts: string[] = [];
      if (urgentIssueJobIds.has(j.id)) parts.push("urgent issue block");
      if (j.materialReadiness === "Missing Items") parts.push("missing materials");
      if (j.permitRequired && (j.permitStatus === "Rejected" || j.permitStatus === "Expired"))
        parts.push(`permit ${j.permitStatus?.toLowerCase()}`);
      return `${j.customerName} (${parts.join(", ")})`;
    });

    return {
      triggered: blocked.length > 0,
      count: blocked.length,
      affectedJobIds: blocked.map((j) => j.id),
      amountAtRisk,
      context:
        blocked.length > 0
          ? `${blocked.length} job${blocked.length > 1 ? "s" : ""} hard-blocked: ${reasons.slice(0, 3).join("; ")}${reasons.length > 3 ? ` and ${reasons.length - 3} more` : ""}`
          : "No blocked jobs detected",
      suggestedAction: "Escalate blocked jobs to department heads — each blocked job delays revenue and downstream scheduling",
      snapshot: {
        blockedJobIds: blocked.map((j) => j.id),
        reasons,
        amountAtRisk,
      },
    };
  },
};
